import type { ApiDashboardElement, IntervalJSON } from '@highimpact-frontend/api-types';

export type ElementMeasurePlotType =
  | 'timeseries'
  | 'scatter'
  | 'windrose'
  | 'bar'
  | 'ombrothermic';

export type ElementMeasureKind = ElementMeasurePlotType | 'table' | 'tablebycampaign';

function isConfiguredAggregatedTable(element: ApiDashboardElement): boolean {
  const properties = element.properties as {
    state_type?: string;
    config?: { type?: string };
  };

  return (
    element.element_type === 'table' &&
    properties.state_type === 'configured_table' &&
    properties.config?.type === 'AggregatedTable'
  );
}

function isConfiguredTableByCampaignElement(
  element: ApiDashboardElement
): boolean {
  const properties = element.properties as { state_type?: string };

  return (
    element.element_type === 'tablebycampaign' &&
    properties.state_type === 'configured_table_by_campaign'
  );
}

/** Returns the configured plot type when the element supports measure requests. */
export function getConfiguredPlotType(
  element: ApiDashboardElement
): ElementMeasurePlotType | undefined {
  const kind = getElementMeasureKind(element);
  if (
    kind === 'table' ||
    kind === 'tablebycampaign' ||
    kind === undefined
  ) {
    return undefined;
  }

  return kind;
}

/** Returns the element-derived measure kind for plots and supported tables. */
export function getElementMeasureKind(
  element: ApiDashboardElement
): ElementMeasureKind | undefined {
  const properties = element.properties as
    | { state_type?: string; plot_type?: string; intervals?: IntervalJSON[] }
    | undefined;

  if (
    element.element_type === 'plot' &&
    properties?.state_type === 'configured_plot' &&
    Array.isArray(properties.intervals)
  ) {
    switch (properties.plot_type) {
      case 'timeseries':
      case 'scatter':
      case 'windrose':
      case 'bar':
      case 'ombrothermic':
        return properties.plot_type;
      default:
        return undefined;
    }
  }

  if (isConfiguredAggregatedTable(element)) {
    return 'table';
  }

  if (isConfiguredTableByCampaignElement(element)) {
    return 'tablebycampaign';
  }

  return undefined;
}

/** Returns true when the element supports element-derived measure requests. */
export function supportsElementMeasureRequest(
  element: ApiDashboardElement
): boolean {
  return getElementMeasureKind(element) !== undefined;
}

/** Multi-interval kinds (one request per interval) */
export function usesMultiIntervalMeasureFetch(
  kind: ElementMeasureKind | undefined
): boolean {
  return kind === 'table' || kind === 'bar';
}

export function isLegacyTableElement(element: ApiDashboardElement): boolean {
  const properties = element.properties as {
    state_type?: string;
    config?: { type?: string };
  };

  return (
    element.element_type === 'table' &&
    properties.state_type === 'configured_table' &&
    properties.config?.type !== 'AggregatedTable'
  );
}
